import { useMemo, useState } from "react";
import { Box, Paper, Typography, Avatar } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { CustomEventType } from "../../utils/types";
import {
    getScheduleTotals,
    groupEventsByUser,
    groupEventsByService,
    sortByTotalHoursDesc,
    sortByUserName,
} from "../../utils/scheduleUtils";
import { HoursPerUser } from "./HoursPerUser";
import { HoursPerService } from "./HoursPerService";

type WeeklyTotalsOverlayProps = {
    events: Array<CustomEventType>;
    open: boolean;
};

export const WeeklyTotalsOverlay = ({ events, open }: WeeklyTotalsOverlayProps) => {
    const theme = useTheme();
    const [showByUser, setShowByUser] = useState(true);
    const [showByService, setShowByService] = useState(true);

    const byUser = useMemo(() => {
        const users = groupEventsByUser(events);
        const sortedUsers = sortByUserName(users);

        const result = sortedUsers.map((user) => {
            const { weeklyTotal } = getScheduleTotals(user.events);
            return { name: user.userName, color: user.userColor || "#44749a", totalHours: Number(weeklyTotal.toFixed(1)) };
        });

        const sortedResult = sortByTotalHoursDesc(result);
        return sortedResult;
    }, [events]);

    const byService = useMemo(() => {
        const services = groupEventsByService(events);

        const result = Object.values(services).map((service) => {
            const weeklyHours = Object.values(service.dailyTotals).reduce((acc, curr) => acc + curr, 0);
            const users = Object.values(service.users).map((user) => {
                const { weeklyTotal } = getScheduleTotals(user.events);
                return { name: user.userName, totalHours: Number(weeklyTotal.toFixed(1)) };
            });

            const sortedUsers = users.sort((a, b) => b.totalHours - a.totalHours);

            return {
                key: String(service.id),
                name: service.name,
                color: service.color,
                possibleSessions: service.possibleSessions.weekly || 0,
                weeklyHours: Number(weeklyHours.toFixed(1)),
                users: sortedUsers,
            };
        });

        const sortedResult = result.sort((a, b) => b.weeklyHours - a.weeklyHours);
        return sortedResult;
    }, [events]);

    if (!open) return null;

    return (
        <Box
            sx={{
                position: "absolute",
                top: 64,
                right: 0,
                bottom: 0,
                width: 320,
                pointerEvents: "auto",
                zIndex: 10,
                p: 1.5,
                boxSizing: "border-box",
            }}
        >
            <Paper
                elevation={4}
                sx={{
                    height: "100%",
                    overflowY: "auto",
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[8],
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.default,
                        borderRadius: "8px 8px 0 0",
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                width: 32,
                                height: 32,
                            }}
                        >
                            <ScheduleIcon fontSize="small" />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                Resumo Semanal
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Horas agendadas
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 1 }}>
                    <HoursPerUser
                        users={byUser}
                        expanded={showByUser}
                        onChange={setShowByUser}
                    />
                    <HoursPerService
                        services={byService}
                        expanded={showByService}
                        onChange={setShowByService}
                    />
                </Box>
            </Paper>
        </Box>
    );
};
